import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { API, graphqlOperation } from 'aws-amplify';

import { getProjects } from 'app/routes/Dashboard/actions/projects';
import { getProjectImgAndShareIcon } from 'utils/projectHelpers';
import { getError, showPrice } from 'utils/appHelpers';
import { searchLikieesInQuery, searchLikieesQuery } from 'graphql/likiees/queries';
import allProjectsImage from 'assets/images/all-projects.jpg';
import { getLocalizedPath } from 'utils/languageHelpers';

import LikieeSelectionModal from './LikieeSelectionModal';
import ContextInputModal from './ContextInputModal';
import ComparisonResultModal from './ComparisonResultModal';
import { addComparisonToHistory, updateComparisonInHistory } from './comparisonStorage';
import { generateMockComparison, transformToComparisonProduct } from './mockComparison';

import './AICompare.scss';

const MODAL_ROOT_ID = 'ai-compare-modal-root';
const MAX_FETCH_LIMIT = 200;

const ensureModalRoot = () => {
  if (typeof document === 'undefined') return null;
  let root = document.getElementById(MODAL_ROOT_ID);
  if (!root) {
    root = document.createElement('div');
    root.id = MODAL_ROOT_ID;
    document.body.appendChild(root);
  }
  return root;
};

const dedupeById = items => {
  const map = new Map();
  items.forEach(item => {
    if (!item || !item.id) return;
    map.set(item.id, item);
  });
  return Array.from(map.values());
};

const AICompare = ({ isOpen, onClose, currentProduct }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const projectsObj = useSelector(state => state.projects);
  const totalLikiees = useSelector(state => state.home?.totalLikiees);
  const [modalRoot, setModalRoot] = useState(null);
  const [currentStep, setCurrentStep] = useState('likiee');
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedLikiees, setSelectedLikiees] = useState([]);
  const [likiees, setLikiees] = useState([]);
  const [likieesLoading, setLikieesLoading] = useState(false);
  const [likieesError, setLikieesError] = useState('');
  const [contextValue, setContextValue] = useState('');
  const [comparisonResult, setComparisonResult] = useState(null);

  const normalizedCurrentProduct = useMemo(
    () =>
      currentProduct
        ? transformToComparisonProduct(currentProduct, {
            isCurrent: true,
            fallbackName: currentProduct?.name || t('ai_compare.current_product', { defaultValue: 'Current Product' }),
            showPriceFn: showPrice,
          })
        : null,
    [currentProduct, t],
  );

  const projectValues = useMemo(() => Object.values(projectsObj || {}), [projectsObj]);

  const projectsList = useMemo(() => {
    const mapped = projectValues.map(project => {
      const { Icon, image } = getProjectImgAndShareIcon(project);
      return {
        id: project.id,
        name: project.name,
        icon: image || Icon,
        itemsCount: project.itemsCount || 0,
        isAllItems: false,
      };
    });

    const totalCount =
      typeof totalLikiees === 'number'
        ? totalLikiees
        : mapped.reduce((acc, project) => acc + (project.itemsCount || 0), 0);

    return [
      {
        id: 'all',
        name: t('ai_compare.all_likiees', { defaultValue: 'All Likiees' }),
        icon: allProjectsImage,
        itemsCount: totalCount,
        isAllItems: true,
      },
      ...mapped,
    ];
  }, [projectValues, totalLikiees, t]);

  useEffect(() => {
    setModalRoot(ensureModalRoot());
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return () => {};
    }

    if (typeof document !== 'undefined') {
      document.body.classList.add('ai-compare-modal-open');
    }

    // Always fetch projects if we don't have any
    if (!projectsObj || Object.keys(projectsObj).length === 0) {
      getProjects();
    }

    setCurrentStep('likiee');
    setSelectedProject(null);
    setLikiees([]);
    setContextValue('');
    setComparisonResult(null);
    setLikieesError('');
    setSelectedLikiees(normalizedCurrentProduct ? [normalizedCurrentProduct] : []);

    return () => {
      if (typeof document !== 'undefined') {
        document.body.classList.remove('ai-compare-modal-open');
      }
    };
  }, [isOpen, normalizedCurrentProduct]);

  useEffect(() => {
    if (!isOpen || currentStep !== 'likiee' || !selectedProject) {
      return () => {};
    }

    let isMounted = true;

    const fetchLikiees = async () => {
      setLikieesLoading(true);
      setLikieesError('');
      try {
        const body = {
          limit: MAX_FETCH_LIMIT,
          sort: [{ field: 'createTime', direction: 'desc' }],
        };

        let query = searchLikieesQuery;
        if (selectedProject?.isAllItems) {
          const projectIds = projectValues.map(project => project.id);
          if (projectIds.length) {
            body.projects = projectIds;
            query = searchLikieesInQuery;
          }
        } else if (selectedProject?.id) {
          body.projects = [selectedProject.id];
          query = searchLikieesInQuery;
        }

        const { data } = await API.graphql(graphqlOperation(query, body));
        const response = data.searchLikieesIn || data.searchLikiees;
        const items = response?.items || [];

        const transformed = items.map(item =>
          transformToComparisonProduct(item, {
            isCurrent: item.id === currentProduct?.id,
            fallbackName: item.name,
            showPriceFn: showPrice,
          }),
        );

        const merged = dedupeById([...transformed, ...selectedLikiees]);
        if (isMounted) {
          setLikiees(merged);
        }
      } catch (err) {
        if (isMounted) {
          setLikieesError(getError(err));
        }
      } finally {
        if (isMounted) {
          setLikieesLoading(false);
        }
      }
    };

    fetchLikiees();
    return () => {
      isMounted = false;
    };
  }, [isOpen, currentStep, selectedProject, projectValues, currentProduct, selectedLikiees]);

  if (!isOpen || !modalRoot) {
    return null;
  }

  const handleClose = () => {
    setCurrentStep('likiee');
    setSelectedProject(null);
    setLikiees([]);
    setSelectedLikiees(normalizedCurrentProduct ? [normalizedCurrentProduct] : []);
    setComparisonResult(null);
    onClose();
  };

  const handleLikieesNext = selectedItems => {
    const normalized = dedupeById(
      selectedItems.map(item => ({ ...item, isCurrent: item.isCurrent || item.id === currentProduct?.id })),
    );
    setSelectedLikiees(normalized);
    setCurrentStep('context');
  };

  const handleContextBack = () => {
    setCurrentStep('likiee');
  };

  const handleContextSubmit = context => {
    setContextValue(context);
    const result = generateMockComparison(selectedLikiees, context, t);
    setComparisonResult(result);
    addComparisonToHistory(result);
    setCurrentStep('result');
  };

  const handleViewHistory = () => {
    handleClose();
    history.push(getLocalizedPath('/comparison-history'));
  };

  const handleBookmarkToggle = updatedComparison => {
    setComparisonResult(updatedComparison);
    updateComparisonInHistory(updatedComparison);
  };

  const handleProjectChange = project => {
    setSelectedProject(project);
    // Don't reset selectedLikiees - keep previously selected items
  };

  const ensureCurrentIncluded = list => {
    if (!normalizedCurrentProduct) return list;
    const withCurrent = dedupeById([normalizedCurrentProduct, ...list]);
    return withCurrent;
  };

  const currentLikiees = ensureCurrentIncluded(likiees);

  let modalContent = null;

  if (currentStep === 'likiee') {
    modalContent = (
      <LikieeSelectionModal
        onClose={handleClose}
        onNext={handleLikieesNext}
        selectedProject={selectedProject}
        projects={projectsList}
        onProjectChange={handleProjectChange}
        likiees={currentLikiees}
        isLoading={likieesLoading}
        error={likieesError}
        initialSelection={selectedLikiees}
      />
    );
  } else if (currentStep === 'context') {
    modalContent = (
      <ContextInputModal
        onClose={handleClose}
        onBack={handleContextBack}
        onCompare={handleContextSubmit}
        selectedLikiees={selectedLikiees}
        initialContext={contextValue}
      />
    );
  } else if (currentStep === 'result' && comparisonResult) {
    modalContent = (
      <ComparisonResultModal
        onClose={handleClose}
        onViewHistory={handleViewHistory}
        comparisonResult={comparisonResult}
        onToggleBookmark={handleBookmarkToggle}
      />
    );
  }

  return createPortal(modalContent, modalRoot);
};

AICompare.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentProduct: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    images: PropTypes.array,
    imageURL: PropTypes.string,
    item: PropTypes.shape({
      mainImage: PropTypes.string,
    }),
    price: PropTypes.number,
    originalPrice: PropTypes.number,
    currency: PropTypes.string,
  }),
};

AICompare.defaultProps = {
  currentProduct: null,
};

export default AICompare;
